/**
 * Platform detection for the install and notifications onboarding.
 *
 * This matters more than usual here. On iOS, web push only works for a PWA that
 * has been added to the home screen (iOS 16.4+), and the permission prompt must
 * follow a user gesture. So "install first, then enable notifications" is not a
 * nicety, it is the only order that works, and the copy has to differ per
 * platform because the steps genuinely differ.
 */

export type Platform = 'ios' | 'android' | 'desktop'

export function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'desktop'
  const ua = navigator.userAgent
  // iPadOS 13+ reports itself as a Mac. Touch points give it away.
  const iPadOS =
    navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1
  if (/iPhone|iPad|iPod/i.test(ua) || iPadOS) return 'ios'
  if (/Android/i.test(ua)) return 'android'
  return 'desktop'
}

/** True once the app is running from the home screen or as an installed window. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true
  return iosStandalone || window.matchMedia('(display-mode: standalone)').matches
}

export function supportsPush(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

export function notificationPermission(): NotificationPermission | 'unsupported' {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported'
  return Notification.permission
}

/**
 * On iOS the install step is a hard prerequisite. On Android and desktop the
 * browser can ask for permission without an install, but an installed app is
 * still far more reliable, so we ask for it first everywhere.
 */
export function installRequiredForPush(platform: Platform): boolean {
  return platform === 'ios'
}

export interface InstallGuide {
  heading: string
  steps: string[]
  note?: string
}

export function installGuide(platform: Platform): InstallGuide {
  switch (platform) {
    case 'ios':
      return {
        heading: 'Add to Home Screen on iPhone or iPad',
        steps: [
          'Make sure you are in Safari. Chrome on iPhone cannot install this app.',
          'Tap the Share button at the bottom of the screen. It is the square with an arrow pointing up.',
          'Scroll down and tap "Add to Home Screen".',
          'Tap "Add" in the top right corner.',
          'Close Safari and open La Folie from your home screen.',
        ],
        note: 'On iPhone, reminders can only reach you after this step. There is no way around it.',
      }
    case 'android':
      return {
        heading: 'Install on Android',
        steps: [
          'Tap the Install button below. If nothing happens, use the steps underneath.',
          'Open the browser menu, the three dots in the top right.',
          'Tap "Install app" or "Add to Home screen".',
          'Confirm, then open La Folie from your home screen.',
        ],
      }
    default:
      return {
        heading: 'Install on this computer',
        steps: [
          'Tap the Install button below if it is available.',
          'Otherwise look for the install icon in the address bar, on the right.',
          'Confirm the install, then open La Folie from your apps.',
        ],
        note: 'Most coaches will use this on a phone. Desktop works, it is just not the main case.',
      }
  }
}

/** Captured beforeinstallprompt event, Chromium only. */
export interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}
